/**
 * Declarative Sim Lab setup interpreter (scenario.setup.kind).
 * Shared helpers fix meaning; kind handlers compose them only.
 * Dummy hostiles reuse `add` sprites (team 1 addArt).
 */
import { raidCatalog } from "../../../data/raid-catalog";
import {
  addThreat,
  createBehaviorState,
  createHitSignal,
  noteHit,
  type AggroTable,
} from "../../../sim";
import { createRaidAggro } from "../raid-aggro";
import { seekAssaultWave, startAssaultEncounter } from "../raid-assault";
import { clampToPlayBounds } from "../raid-rules";
import type { RaidBattleState, RaidUnit } from "../raid-types";
import type {
  SimDummySpec,
  SimSetupDef,
  SimSetupRuntime,
  SimStageDef,
} from "./sim-lab-types";

/** CD-only boss freeze ??skills never fire; boss may still chase/orbit. */
export function freezeBossSkills(state: RaidBattleState): void {
  state.boss.atkCd = 1e9;
  state.boss.brassCd = 1e9;
  state.boss.leapCd = 1e9;
  state.boss.meleeCd = 1e9;
}

function hideBossForSim(state: RaidBattleState): void {
  freezeBossSkills(state);
  state.encounter.bossCombatArmed = false;
  state.boss.x = -500;
  state.boss.y = -500;
  state.boss.leap = null;
  state.boss.telegraph = null;
}

function disarmFocus(focus: RaidUnit): void {
  focus.damage = 0;
  focus.attackCdLeft = 1e9;
  focus.skillCdLeft = 1e9;
}

/**
 * Lab pressure dummy ??same sprite path as battle adds (`defId: add`).
 * Name makes the Sim Lab role explicit (not a real add spawn path).
 */
export function spawnPressureDummy(
  state: RaidBattleState,
  x: number,
  y: number,
  spec: Partial<SimDummySpec> = {},
): RaidUnit {
  const id = state.nextId++;
  const hp = spec.hp ?? 80;
  const damage = spec.damage ?? 0;
  const disarm = spec.disarm ?? damage === 0;
  const u: RaidUnit = {
    id,
    defId: "add",
    classId: "dps",
    team: 1,
    x,
    y,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    hp,
    maxHp: hp,
    damage,
    radius: 14,
    speed: spec.speed ?? 0,
    attackRange: 28,
    attackCd: 1.0,
    attackCdLeft: disarm ? 1e9 : 0.2,
    skillId: null,
    skillCdLeft: 0,
    skillCdMax: 0,
    attackAnimT: 0,
    stunnedUntil: 0,
    knock: null,
    aimX: x,
    aimY: y - 1,
    brain: {
      select: createBehaviorState(),
      targetId: -1,
      nextDecideAt: 0,
      order: { current: null },
      gestureAnchor: null,
      dangerReactAt: 0,
      engageRangeIds: [],
      softSpaceLatch: false,
      thrallEscapeSeat: null,
      thrallEscapeAa: 0,
    },
    hit: createHitSignal(),
    barkCdLeft: 0,
    aggro: createRaidAggro(),
    telegraph: null,
    leap: null,
    brassCd: 0,
    leapCd: 0,
    chargeCd: 0,
    toughness: 0,
    toughnessMax: 0,
    breakStunDuration: 0,
    combatArmed: true,
    dropRemain: 0,
  };
  state.units.push(u);
  return u;
}

/** Seed threat on focus?�target (engage sticky / peel labs). */
export function seedFocusThreat(
  focusAggro: AggroTable,
  targetId: number,
  amount: number,
): void {
  addThreat(focusAggro, targetId, amount);
}

function snapFocusToAnchor(focus: RaidUnit, stage: SimStageDef): void {
  focus.x = stage.anchors.focus.x;
  focus.y = stage.anchors.focus.y;
  focus.aimX = focus.x;
  focus.aimY = focus.y - 1;
}

function applyFocusOffset(
  focus: RaidUnit,
  setup: SimSetupDef,
): void {
  const o = setup.focusOffset;
  if (!o) return;
  focus.x += o.dx;
  focus.y += o.dy;
  focus.aimX = focus.x;
  focus.aimY = focus.y - 1;
}

function placeBossAtAnchor(state: RaidBattleState, stage: SimStageDef): void {
  const a = stage.anchors.boss ?? stage.anchors.focus;
  state.boss.x = a.x;
  state.boss.y = a.y;
}

/** Park ally at stage ally anchor; disarm + speed 0 so they do not drift. */
export function parkAlly(
  state: RaidBattleState,
  defId: string,
  stage: SimStageDef,
  offset?: { dx: number; dy: number },
): RaidUnit {
  const ally = state.units.find((u) => u.defId === defId && u.team === 0);
  if (!ally) throw new Error(`sim-setup: ally ${defId} not in party`);
  const base = stage.anchors.ally ?? {
    x: stage.anchors.focus.x,
    y: stage.anchors.focus.y - 180,
  };
  ally.x = base.x + (offset?.dx ?? 0);
  ally.y = base.y + (offset?.dy ?? 0);
  ally.aimX = ally.x;
  ally.aimY = ally.y - 1;
  ally.damage = 0;
  ally.attackCdLeft = 1e9;
  ally.speed = 0;
  ally.vx = 0;
  ally.vy = 0;
  ally.intentVx = 0;
  ally.intentVy = 0;
  return ally;
}

function applyLeapHold(state: RaidBattleState, focus: RaidUnit): void {
  freezeBossSkills(state);
  const leapDef = raidCatalog.bosses.bosses.colossus.leap;
  // Land on the focus pad (feet-in). Do NOT park south of the disk � that
  // radial-out escape biased dodge goals "below" the telegraph in Sim Lab.
  // Instinct lag stays measurable via reactionDelaySec on feet-in.
  const landX = focus.x;
  const landY = focus.y;
  state.boss.leap = {
    phase: "windup",
    remain: leapDef.windupSec,
    duration: leapDef.windupSec,
    landX,
    landY,
    fromX: state.boss.x,
    fromY: state.boss.y,
    landRadius: leapDef.landRadius,
    landDamage: leapDef.landDamage,
    danger: "red",
    purpose: "combat",
  };
}

function applySurviveHit(
  state: RaidBattleState,
  focus: RaidUnit,
  setup: Extract<SimSetupDef, { kind: "survive_hit" }>,
  stage: SimStageDef,
): SimSetupRuntime {
  if (setup.disarmFocus) disarmFocus(focus);
  focus.hp = focus.maxHp * setup.focusHpFrac;
  const dmg = setup.add.damage ?? 0;
  const add = spawnPressureDummy(
    state,
    focus.x + setup.add.dx,
    focus.y + setup.add.dy,
    {
      hp: setup.add.hp ?? 50_000,
      damage: dmg,
      disarm: dmg <= 0,
      speed: 0,
    },
  );
  if (setup.add.hitPulse != null) {
    noteHit(focus.hit, setup.add.hitPulse, add.id, state.time);
  }
  const rt: SimSetupRuntime = {};
  const healer = state.units.find(
    (u) => u.team === 0 && u.classId === "healer" && u.hp > 0 && u.id !== focus.id,
  );
  if (healer) {
    parkAlly(state, healer.defId, stage, { dx: -40, dy: -80 });
    const parked = state.units.find((u) => u.id === healer.id)!;
    rt.allyId = parked.id;
    rt.pinAlly = { x: parked.x, y: parked.y };
  }
  if (setup.pulseHit) {
    rt.pulseHit = {
      attackerId: add.id,
      everySec: setup.pulseHit.everySec,
      amount: setup.pulseHit.amount,
    };
  }
  return rt;
}

/**
 * Pin boss: skills frozen + speed/vel 0 + bounds settle.
 * Unlike freezeBossSkills alone, this prevents chase/orbit flee reads.
 */
export function pinBossStationary(state: RaidBattleState): { x: number; y: number } {
  freezeBossSkills(state);
  const boss = state.boss;
  boss.speed = 0;
  boss.vx = 0;
  boss.vy = 0;
  boss.intentVx = 0;
  boss.intentVy = 0;
  const p = clampToPlayBounds(boss.x, boss.y, boss.radius);
  boss.x = p.x;
  boss.y = p.y;
  return { x: boss.x, y: boss.y };
}

function applyKitePressure(
  state: RaidBattleState,
  focus: RaidUnit,
  setup: Extract<SimSetupDef, { kind: "kite_pressure" }>,
): SimSetupRuntime {
  const motion = setup.bossMotion ?? "pin";
  const rt: SimSetupRuntime = {};
  if (motion === "pin") {
    rt.pinBoss = pinBossStationary(state);
  } else {
    freezeBossSkills(state);
  }
  if (setup.disarmFocus) disarmFocus(focus);
  for (const d of setup.dummies) {
    const dmg = d.damage ?? 0;
    const speed = d.speed ?? 0;
    const add = spawnPressureDummy(state, focus.x + d.dx, focus.y + d.dy, {
      hp: d.hp ?? 50_000,
      damage: dmg,
      disarm: d.disarm ?? dmg <= 0,
      speed,
    });
    if (speed > 0) {
      addThreat(add.aggro, focus.id, d.seedThreat ?? 200);
      if (rt.chaseDummyIds == null) rt.chaseDummyIds = [];
      rt.chaseDummyIds.push(add.id);
    }
  }
  return rt;
}

function applyStickyPeel(
  state: RaidBattleState,
  focus: RaidUnit,
  setup: Extract<SimSetupDef, { kind: "sticky_peel" }>,
): SimSetupRuntime {
  const frac = setup.primary.standRangeFrac;
  const stand = Math.min(focus.attackRange * frac, focus.attackRange * 0.92);
  const primary = spawnPressureDummy(state, focus.x, focus.y - stand, {
    hp: setup.primary.hp,
    damage: 0,
    disarm: true,
  });
  focus.brain.targetId = primary.id;
  focus.brain.engageRangeIds = [primary.id];
  seedFocusThreat(focus.aggro, primary.id, setup.primary.seedThreat ?? 220);

  const chase = setup.peelChase === true;
  const peelX = chase
    ? focus.x + (setup.peel.dx !== 0 ? setup.peel.dx : 160)
    : setup.peel.dx !== 0 || setup.peel.dy !== 0
      ? focus.x + setup.peel.dx
      : focus.x + stand;
  const peelY = chase
    ? focus.y + (setup.peel.dy !== 0 ? setup.peel.dy : 0)
    : setup.peel.dx !== 0 || setup.peel.dy !== 0
      ? focus.y + setup.peel.dy
      : focus.y;
  const peelDmg = setup.peel.damage ?? (chase ? 8 : 0);
  const peel = spawnPressureDummy(state, peelX, peelY, {
    hp: setup.peel.hp ?? 200,
    damage: peelDmg,
    disarm: setup.peel.disarm ?? peelDmg <= 0,
    speed: chase ? (setup.peel.speed ?? 55) : 0,
  });
  const peelThreat = setup.peel.seedThreat ?? (chase ? 40 : 260);
  seedFocusThreat(focus.aggro, peel.id, peelThreat);
  if (chase) addThreat(peel.aggro, focus.id, Math.max(peelThreat, 120));
  const rt: SimSetupRuntime = {
    primaryId: primary.id,
    pinPrimary: { x: primary.x, y: primary.y },
  };
  if (chase) rt.peelChaseId = peel.id;
  return rt;
}

function applyAssaultWave(
  state: RaidBattleState,
  setup: Extract<SimSetupDef, { kind: "assault_wave" }>,
): SimSetupRuntime {
  startAssaultEncounter(state, setup.scriptId);
  seekAssaultWave(state, setup.waveIndex);
  return {};
}

export type ApplySimSetupOpts = {
  /**
   * Keep createBattleState party seats (play-map gate). Default snaps focus
   * to stage.anchors for Sim Lab pad demos.
   */
  preserveFormation?: boolean;
};

/** Place focus/boss on stage anchors, then apply setup.kind (composition only). */
export function applySimSetup(
  state: RaidBattleState,
  focus: RaidUnit,
  stage: SimStageDef,
  setup: SimSetupDef,
  opts: ApplySimSetupOpts = {},
): SimSetupRuntime {
  if (!opts.preserveFormation) {
    snapFocusToAnchor(focus, stage);
    applyFocusOffset(focus, setup);
  }
  if (setup.kind === "assault_wave") {
    // Real assault parks the boss; do not placeBossAtAnchor / hideBoss.
    return applyAssaultWave(state, setup);
  }
  if ("hideBoss" in setup && setup.hideBoss) {
    hideBossForSim(state);
  } else {
    placeBossAtAnchor(state, stage);
  }

  switch (setup.kind) {
    case "leap_hold":
      applyLeapHold(state, focus);
      return {};
    case "survive_hit":
      return applySurviveHit(state, focus, setup, stage);
    case "kite_pressure":
      return applyKitePressure(state, focus, setup);
    case "sticky_peel":
      return applyStickyPeel(state, focus, setup);
    default: {
      const _exhaustive: never = setup;
      throw new Error(`unhandled setup: ${(_exhaustive as SimSetupDef).kind}`);
    }
  }
}
